import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { toastApiError } from "../utils/toastErrors";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import {
  ArrowRight,
  Banknote,
  Building2,
  CalendarDays,
  FileText,
  Globe,
  Hash,
  Package,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import {
  deletePerformas,
  fetchOrderById,
  updateOrderStatus,
} from "../actions/performaActions";
import { formatNumber } from "../utils/numberFormat";
import { LazyLoading } from "./ui/LazyLoading";

const cx = (...classes) => classes.filter(Boolean).join(" ");
const safeStr = (value) =>
  value === null || value === undefined ? "" : String(value);
const sanitizeNumeric = (value) => safeStr(value).replace(/[^\d]/g, "");
const sanitizeDecimal = (value) => {
  const normalized = safeStr(value).replace(/[^\d.]/g, "");
  const [whole = "", ...fractionParts] = normalized.split(".");
  return fractionParts.length ? `${whole}.${fractionParts.join("")}` : whole;
};
const formatGoodNumber = (value, maximumFractionDigits = 3) =>
  value === null || value === undefined || value === ""
    ? "—"
    : formatNumber(value, { maximumFractionDigits });

const createDraftFromOrder = (order = {}) => ({
  prf_number: safeStr(order.prf_number),
  goods_type: safeStr(order.goods_type),
  payment_instrument: safeStr(order.payment_instrument),
  bank_info: safeStr(order.bank_info),
  FOB: safeStr(order.FOB),
  prf_freight_price: safeStr(order.prf_freight_price),
  prf_total_price: safeStr(order.prf_total_price),
  prf_currency_type: safeStr(order.prf_currency_type),
  currency_allocation_status: safeStr(order.currency_allocation_status),
  prf_currency_price: safeStr(order.prf_currency_price),
  rial_deposit: safeStr(order.rial_deposit),
  prf_seller_country: safeStr(order.prf_seller_country),
  prf_date: safeStr(order.prf_date),
  prf_expire_date: safeStr(order.prf_expire_date),
});

const Chip = ({ tone = "slate", children }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold",
        tones[tone] || tones.slate,
      )}
    >
      {children}
    </span>
  );
};

const Card = ({ title, icon: Icon, className = "", children, actions }) => (
  <div
    dir="rtl"
    className={cx(
      "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden",
      className,
    )}
  >
    <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">{actions}</div>
        <div className="ml-auto flex items-center gap-3">
          {Icon ? (
            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon size={20} />
            </div>
          ) : null}
          <h3 className="text-base sm:text-lg font-extrabold text-slate-800 text-right">
            {title}
          </h3>
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoRow = ({ label, value, icon: Icon, tone }) => (
  <div className="flex items-start justify-between gap-3 py-2.5">
    <div className="text-right">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-bold text-slate-900 break-words">
        {tone ? <Chip tone={tone}>{value}</Chip> : value}
      </div>
    </div>
    {Icon ? (
      <div className="mt-0.5 text-slate-400">
        <Icon size={18} />
      </div>
    ) : null}
  </div>
);

const ModalShell = ({
  open,
  title,
  icon: Icon,
  onClose,
  children,
  footer,
  maxWidth = "max-w-4xl",
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="button"
        tabIndex={-1}
      />
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div
          dir="rtl"
          className={cx(
            "w-full rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden",
            maxWidth,
          )}
        >
          <div className="px-5 sm:px-6 py-4 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={18} />
                بستن
              </button>
              <div className="ml-auto flex items-center gap-3">
                {Icon ? (
                  <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                ) : null}
                <div className="text-right">
                  <div className="text-lg font-extrabold text-slate-800">
                    {title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    مقادیر را ویرایش کنید و ذخیره بزنید.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6 max-h-[75vh] overflow-auto">
            {children}
          </div>
          {footer ? (
            <div className="px-5 sm:px-6 py-4 border-t border-slate-200 bg-white">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Field = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  icon: Icon,
  inputMode,
  type = "text",
}) => (
  <div className="space-y-1.5">
    <label
      htmlFor={id}
      className="block text-sm font-medium text-slate-700 text-right"
    >
      {label}
    </label>
    <div className="relative">
      {Icon ? (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          <Icon size={18} />
        </span>
      ) : null}
      <input
        id={id}
        name={name}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={cx(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
          "placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
          Icon ? "pr-10" : "pr-3",
        )}
      />
    </div>
  </div>
);

const DateField = ({ label, value, onChange, icon: Icon, portal = false }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-slate-700 text-right">
      {label}
    </label>
    <div className="relative">
      {Icon ? (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Icon size={18} />
        </span>
      ) : null}
      <DatePicker
        calendar={persian}
        locale={persian_fa}
        value={value}
        onChange={(val) => onChange(val ? val.format("YYYY-MM-DD") : "")}
        format="YYYY-MM-DD"
        inputClass={cx(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
          "placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
        )}
        containerClassName="w-full"
        calendarPosition="bottom-right"
        portal={portal}
      />
    </div>
  </div>
);

export default function RegedOrderDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { order, loading, error } = useSelector(
    (state) => state.orderDetails || {},
  );

  const prfOrderNo = useMemo(
    () => location.pathname.split("/").filter(Boolean).pop(),
    [location.pathname],
  );

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draft, setDraft] = useState(createDraftFromOrder());
  const [purchaseDraft, setPurchaseDraft] = useState({
    purchased_amount: "",
    purchase_date: "",
    purchase_price: "",
  });
  const [editingPurchaseId, setEditingPurchaseId] = useState(null);
  const [purchaseSaving, setPurchaseSaving] = useState(false);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  useEffect(() => {
    if (prfOrderNo && prfOrderNo !== "undefined") {
      dispatch(fetchOrderById(prfOrderNo));
      return;
    }
    navigate("/error");
  }, [dispatch, navigate, prfOrderNo]);

  useEffect(() => {
    if (order) {
      setDraft(createDraftFromOrder(order));
    }
  }, [order]);

  const remainingTotal = Number(order?.remaining_total ?? 0);
  const remainingTone = remainingTotal >= 0 ? "green" : "red";
  const goods = useMemo(
    () => (Array.isArray(order?.goods) ? order.goods : []),
    [order?.goods],
  );
  const currencyPurchases = useMemo(
    () =>
      Array.isArray(order?.currency_purchases) ? order.currency_purchases : [],
    [order?.currency_purchases],
  );
  const goodsTotals = useMemo(
    () =>
      goods.reduce(
        (totals, good) => ({
          quantity: totals.quantity + (Number(good.quantity) || 0),
          fob: totals.fob + (Number(good.fob_value) || 0),
          netWeight: totals.netWeight + (Number(good.net_weight) || 0),
          grossWeight: totals.grossWeight + (Number(good.gross_weight) || 0),
        }),
        { quantity: 0, fob: 0, netWeight: 0, grossWeight: 0 },
      ),
    [goods],
  );

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleDraftNumeric = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: sanitizeNumeric(value) }));
  };

  const resetPurchaseForm = () => {
    setPurchaseDraft({
      purchased_amount: "",
      purchase_date: "",
      purchase_price: "",
    });
    setEditingPurchaseId(null);
  };

  const openNewPurchase = () => {
    resetPurchaseForm();
    setPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setPurchaseModalOpen(false);
    resetPurchaseForm();
  };

  const handlePurchaseSave = async (event) => {
    event.preventDefault();
    if (
      !purchaseDraft.purchased_amount ||
      !purchaseDraft.purchase_date ||
      !purchaseDraft.purchase_price
    ) {
      toast.error("مبلغ خرید، تاریخ خرید و قیمت خرید را وارد کنید.");
      return;
    }

    setPurchaseSaving(true);
    try {
      const payload = {
        ...purchaseDraft,
        ...(editingPurchaseId ? {} : { proforma: order.id }),
      };
      if (editingPurchaseId) {
        await axiosInstance.patch(
          `/proforma-currency-purchases/${editingPurchaseId}/`,
          payload,
        );
      } else {
        await axiosInstance.post("/proforma-currency-purchases/", payload);
      }
      await dispatch(fetchOrderById(prfOrderNo));
      resetPurchaseForm();
      setPurchaseModalOpen(false);
      toast.success("خرید ارز ذخیره شد.");
    } catch (purchaseError) {
      toastApiError(
        purchaseError,
        { purchase_date: "تاریخ خرید", purchased_amount: "مبلغ خرید", purchase_price: "قیمت خرید" },
        "ذخیره خرید ارز ناموفق بود.",
      );
    } finally {
      setPurchaseSaving(false);
    }
  };

  const startPurchaseEdit = (purchase) => {
    setEditingPurchaseId(purchase.id);
    setPurchaseDraft({
      purchased_amount: safeStr(purchase.purchased_amount),
      purchase_date: safeStr(purchase.purchase_date),
      purchase_price: safeStr(purchase.purchase_price),
    });
    setPurchaseModalOpen(true);
  };

  const deletePurchase = async (purchase) => {
    if (!window.confirm("این خرید ارز حذف شود؟")) return;
    try {
      await axiosInstance.delete(
        `/proforma-currency-purchases/${purchase.id}/`,
      );
      await dispatch(fetchOrderById(prfOrderNo));
      if (editingPurchaseId === purchase.id) resetPurchaseForm();
    } catch (purchaseError) {
      toastApiError(purchaseError, {}, "حذف خرید ارز ناموفق بود.");
    }
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateOrderStatus(prfOrderNo, {
          ...draft,
          rial_deposit: draft.rial_deposit === "" ? null : draft.rial_deposit,
        }),
      );
      await dispatch(fetchOrderById(prfOrderNo));
      setEditOpen(false);
      toast.success("تغییرات ثبت سفارش ذخیره شد.");
    } catch (saveError) {
      toastApiError(saveError, {}, "ذخیره تغییرات ثبت سفارش ناموفق بود.");
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deletePerformas([prfOrderNo]));
      setDeleteOpen(false);
      navigate("/reged-orders");
    } catch (deleteError) {
      setDeleteOpen(false);
      toastApiError(deleteError, {}, "حذف ثبت سفارش ناموفق بود.");
    }
  };

  if (loading) return <LazyLoading />;
  if (error)
    return <div className="mt-24 px-4 text-rose-700">خطا: {String(error)}</div>;
  if (!order || Object.keys(order).length === 0) {
    return <div className="mt-24 px-4 text-slate-700">اطلاعاتی موجود نیست</div>;
  }

  return (
    <div className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 px-3 sm:px-6 font-vazirmatn">
      <div dir="rtl" className="max-w-6xl mx-auto">
        <div className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur shadow-lg overflow-hidden">
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/reged-orders")}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <ArrowRight size={18} />
                بازگشت
              </button>

              <div className="ml-auto flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <FileText size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">
                    جزئیات ثبت سفارش
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    شماره ثبت سفارش:{" "}
                    <span className="font-bold text-slate-700">
                      {order.prf_order_no || prfOrderNo}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-7 py-4 bg-white">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Chip tone="blue">وضعیت: {order.prf_status || "—"}</Chip>
                <Chip tone="green">
                  وضعیت تخصیص ارز: {order.currency_allocation_status || "—"}
                </Chip>
                <Chip tone={remainingTone}>
                  {remainingTotal >= 0
                    ? `باقی‌مانده طبق کوتاژ: ${formatNumber(remainingTotal)}`
                    : `مابه‌التفاوت: ${formatNumber(Math.abs(remainingTotal))}`}
                </Chip>
                <Chip tone="amber">
                  باقی‌مانده منشا ارز: {safeStr(order.remaining_co) || "—"}
                </Chip>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm"
                >
                  <Pencil size={18} />
                  ویرایش
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition shadow-sm"
                >
                  <Trash2 size={18} />
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <Card title="اطلاعات پایه" icon={Hash} className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <InfoRow
                  label="شماره ثبت سفارش"
                  value={safeStr(order.prf_order_no) || "—"}
                  icon={Hash}
                />
                <InfoRow
                  label="شماره پرفورم"
                  value={safeStr(order.prf_number) || "—"}
                  icon={FileText}
                />
                <InfoRow
                  label="شماره پرونده"
                  value={safeStr(order.prfVCodeInt) || "—"}
                  icon={Hash}
                />
                <InfoRow
                  label="نوع کالا"
                  value={safeStr(order.goods_type) || "—"}
                  icon={Package}
                />
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <InfoRow
                  label="ابزار پرداخت"
                  value={safeStr(order.payment_instrument) || "—"}
                  icon={Banknote}
                />
                <InfoRow
                  label="اطلاعات بانک"
                  value={safeStr(order.bank_info) || "—"}
                  icon={Building2}
                />
                <InfoRow
                  label="کشور فروشنده"
                  value={safeStr(order.prf_seller_country) || "—"}
                  icon={Globe}
                />
              </div>
            </div>
          </Card>

          <Card title="مالی و تاریخ‌ها" icon={Banknote}>
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">FOB</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.FOB)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">کرایه حمل</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.prf_freight_price)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">ارزش کل</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.prf_total_price)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    جمع ارز خریداری‌شده
                  </span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.purchased_total)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">ریال واریزی</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.rial_deposit)}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">نوع ارز</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {safeStr(order.prf_currency_type) || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">نرخ ارز خرید</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {formatNumber(order.prf_currency_price)}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">تاریخ سفارش</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {safeStr(order.prf_date) || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">اعتبار سفارش</span>
                  <span className="text-sm font-extrabold text-slate-900">
                    {safeStr(order.prf_expire_date) || "—"}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-4">
          <Card
            title="خریدهای ارز"
            icon={Banknote}
            actions={
              <button
                type="button"
                onClick={openNewPurchase}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-bold text-white hover:opacity-95"
              >
                <Plus size={17} />
                ثبت خرید
              </button>
            }
          >
            <div className="mb-3 flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-500">
                {safeStr(order.prf_currency_type) || "—"}
              </span>
              <span className="font-extrabold text-slate-900">
                {formatNumber(order.purchased_total)}
              </span>
            </div>

            {currencyPurchases.length ? (
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {currencyPurchases.map((purchase) => (
                  <div
                    key={purchase.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50"
                  >
                    <span className="shrink-0 text-xs text-slate-500">
                      {purchase.purchase_date}
                    </span>
                    <span
                      className="min-w-0 flex-1 text-sm font-bold text-slate-900"
                      dir="ltr"
                    >
                      {formatNumber(purchase.purchased_amount)}
                    </span>
                    <span className="shrink-0 text-xs text-slate-500" dir="ltr">
                      {formatNumber(purchase.purchase_price)}
                    </span>
                    <button
                      type="button"
                      title="ویرایش خرید"
                      onClick={() => startPurchaseEdit(purchase)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-sky-700 hover:bg-sky-100"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      title="حذف خرید"
                      onClick={() => deletePurchase(purchase)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-5 text-center text-sm text-slate-500">
                خرید ارزی ثبت نشده است.
              </div>
            )}
          </Card>
        </div>

        <div className="mt-4">
          <Card title={`کالاهای پروفرما (${goods.length})`} icon={Package}>
            {goods.length > 0 ? (
              <>
                <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-4">
                  {[
                    ["جمع تعداد", formatGoodNumber(goodsTotals.quantity)],
                    ["جمع FOB", formatGoodNumber(goodsTotals.fob, 2)],
                    [
                      "جمع وزن خالص",
                      `${formatGoodNumber(goodsTotals.netWeight)} kg`,
                    ],
                    [
                      "جمع وزن ناخالص",
                      `${formatGoodNumber(goodsTotals.grossWeight)} kg`,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-slate-50 px-4 py-3 text-right"
                    >
                      <div className="text-xs text-slate-500">{label}</div>
                      <div
                        className="mt-1 text-sm font-extrabold text-slate-900"
                        dir="ltr"
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full min-w-[1120px] border-collapse bg-white">
                    <thead className="bg-slate-50">
                      <tr className="text-slate-700">
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          ردیف
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          کد تعرفه
                        </th>
                        <th className="w-[34%] border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          شرح تجاری کالا
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          تعداد
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          واحد
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          FOB
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          وزن خالص
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          وزن ناخالص
                        </th>
                        <th className="border-b border-slate-200 p-3 text-right text-xs font-extrabold">
                          کشور مبدا
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {goods.map((good, index) => (
                        <tr
                          key={good.upstream_id || good.id}
                          className="align-top hover:bg-slate-50"
                        >
                          <td className="border-b border-slate-100 p-3 text-sm text-slate-500">
                            {index + 1}
                          </td>
                          <td
                            className="border-b border-slate-100 p-3 text-sm font-bold text-slate-900"
                            dir="ltr"
                          >
                            {good.hs_code || "—"}
                          </td>
                          <td className="whitespace-normal border-b border-slate-100 p-3 text-sm leading-7 text-slate-700">
                            {good.commercial_description || "—"}
                          </td>
                          <td
                            className="border-b border-slate-100 p-3 text-sm text-slate-800"
                            dir="ltr"
                          >
                            {formatGoodNumber(good.quantity)}
                          </td>
                          <td className="border-b border-slate-100 p-3 text-sm text-slate-700">
                            {good.unit_name || "—"}
                          </td>
                          <td
                            className="border-b border-slate-100 p-3 text-sm text-slate-800"
                            dir="ltr"
                          >
                            {formatGoodNumber(good.fob_value, 2)}
                          </td>
                          <td
                            className="border-b border-slate-100 p-3 text-sm text-slate-800"
                            dir="ltr"
                          >
                            {formatGoodNumber(good.net_weight)} kg
                          </td>
                          <td
                            className="border-b border-slate-100 p-3 text-sm text-slate-800"
                            dir="ltr"
                          >
                            {formatGoodNumber(good.gross_weight)} kg
                          </td>
                          <td className="border-b border-slate-100 p-3 text-sm text-slate-700">
                            {good.origin_countries || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">
                کالایی برای این پروفرما همگام‌سازی نشده است.
              </div>
            )}
          </Card>
        </div>

        <div className="mt-4">
          <Card title="لیست سندها" icon={FileText}>
            {order.pre_cottages && order.pre_cottages.length > 0 ? (
              <div className="overflow-auto rounded-2xl border border-slate-200">
                <table className="min-w-[900px] w-full border-collapse bg-white">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-700">
                      <th className="p-3 text-right text-xs font-extrabold border-b border-slate-200">
                        کد یکتای منشا ارز
                      </th>
                      <th className="p-3 text-right text-xs font-extrabold border-b border-slate-200">
                        شماره کوتاژ
                      </th>
                      <th className="p-3 text-right text-xs font-extrabold border-b border-slate-200">
                        تاریخ کوتاژ
                      </th>
                      <th className="p-3 text-right text-xs font-extrabold border-b border-slate-200">
                        مبلغ کوتاژ
                      </th>
                      <th className="p-3 text-right text-xs font-extrabold border-b border-slate-200">
                        نام مشتری
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.pre_cottages.map((pre) => {
                      const cottage = pre.cottage;
                      return (
                        <tr key={pre.co_code} className="hover:bg-slate-50">
                          <td className="p-3 text-sm border-b border-slate-100">
                            <Link
                              to={`/pre_cottages/${pre.co_code}`}
                              className="text-primary hover:underline font-bold"
                            >
                              {pre.co_code}
                            </Link>
                          </td>
                          <td className="p-3 text-sm border-b border-slate-100">
                            {cottage ? (
                              <Link
                                to={`/cottages/${cottage.cottage_number}`}
                                className="text-primary hover:underline font-bold"
                              >
                                {cottage.cottage_number}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="p-3 text-sm border-b border-slate-100">
                            {cottage?.cottage_date || "—"}
                          </td>
                          <td className="p-3 text-sm border-b border-slate-100">
                            {formatNumber(
                              cottage ? cottage.total_value : pre.total_value,
                            )}
                          </td>
                          <td className="p-3 text-sm border-b border-slate-100">
                            {cottage?.cottage_customer ? (
                              <Link
                                to={`/customers/details/${cottage.cottage_customer}`}
                                className="text-primary hover:underline font-bold"
                              >
                                {cottage.cottage_customer_full_name || "نامشخص"}
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                هیچ کوتاژی ثبت نشده است.
              </div>
            )}
          </Card>
        </div>
      </div>

      <ModalShell
        open={purchaseModalOpen}
        title={editingPurchaseId ? "ویرایش خرید ارز" : "ثبت خرید ارز"}
        icon={Banknote}
        onClose={closePurchaseModal}
        maxWidth="max-w-xl"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closePurchaseModal}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              انصراف
            </button>
            <button
              type="submit"
              form="currency-purchase-form"
              disabled={purchaseSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white hover:opacity-95 disabled:opacity-60"
            >
              <Save size={18} />
              {purchaseSaving ? "در حال ذخیره" : "ذخیره"}
            </button>
          </div>
        }
      >
        <form
          id="currency-purchase-form"
          onSubmit={handlePurchaseSave}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <Field
            id="purchased_amount"
            name="purchased_amount"
            label="مبلغ خرید"
            placeholder="عدد"
            value={purchaseDraft.purchased_amount}
            onChange={(event) =>
              setPurchaseDraft((prev) => ({
                ...prev,
                purchased_amount: sanitizeDecimal(event.target.value),
              }))
            }
            icon={Banknote}
            inputMode="decimal"
          />
          <DateField
            label="تاریخ خرید"
            value={purchaseDraft.purchase_date}
            onChange={(value) =>
              setPurchaseDraft((prev) => ({ ...prev, purchase_date: value }))
            }
            icon={CalendarDays}
            portal
          />
          <Field
            id="purchase_price"
            name="purchase_price"
            label="قیمت خرید"
            placeholder="عدد"
            value={purchaseDraft.purchase_price}
            onChange={(event) =>
              setPurchaseDraft((prev) => ({
                ...prev,
                purchase_price: sanitizeDecimal(event.target.value),
              }))
            }
            icon={Banknote}
            inputMode="decimal"
          />
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-slate-700 text-right">
              نوع ارز
            </span>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-bold text-slate-700">
              {safeStr(order.prf_currency_type) || "—"}
            </div>
          </div>
        </form>
      </ModalShell>

      <ModalShell
        open={editOpen}
        title="ویرایش ثبت سفارش"
        icon={Pencil}
        onClose={() => setEditOpen(false)}
        footer={
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setDraft(createDraftFromOrder(order))}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
            >
              بازنشانی
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-extrabold text-white bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm"
            >
              <Save size={18} />
              ذخیره تغییرات
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            id="prf_number"
            name="prf_number"
            label="شماره پرفورم"
            placeholder="مثلاً 12345"
            value={draft.prf_number}
            onChange={handleDraftChange}
            icon={FileText}
          />
          <Field
            id="goods_type"
            name="goods_type"
            label="نوع کالا"
            placeholder="مثلاً مواد اولیه"
            value={draft.goods_type}
            onChange={handleDraftChange}
            icon={Package}
          />
          <Field
            id="payment_instrument"
            name="payment_instrument"
            label="ابزار پرداخت"
            placeholder="..."
            value={draft.payment_instrument}
            onChange={handleDraftChange}
            icon={Banknote}
          />
          <Field
            id="bank_info"
            name="bank_info"
            label="اطلاعات بانک"
            placeholder="..."
            value={draft.bank_info}
            onChange={handleDraftChange}
            icon={Building2}
          />
          <Field
            id="FOB"
            name="FOB"
            label="FOB"
            placeholder="عدد"
            value={draft.FOB}
            onChange={handleDraftNumeric}
            icon={Banknote}
            inputMode="numeric"
          />
          <Field
            id="prf_freight_price"
            name="prf_freight_price"
            label="کرایه حمل"
            placeholder="عدد"
            value={draft.prf_freight_price}
            onChange={handleDraftNumeric}
            icon={Banknote}
            inputMode="numeric"
          />
          <Field
            id="prf_total_price"
            name="prf_total_price"
            label="ارزش کل"
            placeholder="عدد"
            value={draft.prf_total_price}
            onChange={handleDraftNumeric}
            icon={Banknote}
            inputMode="numeric"
          />
          <Field
            id="prf_currency_type"
            name="prf_currency_type"
            label="نوع ارز"
            placeholder="مثلاً USD"
            value={draft.prf_currency_type}
            onChange={handleDraftChange}
            icon={Globe}
          />
          <Field
            id="currency_allocation_status"
            name="currency_allocation_status"
            label="وضعیت تخصیص ارز"
            placeholder="مثلاً تخصیص یافته"
            value={draft.currency_allocation_status}
            onChange={handleDraftChange}
            icon={Banknote}
          />
          <Field
            id="prf_currency_price"
            name="prf_currency_price"
            label="نرخ ارز خرید"
            placeholder="عدد"
            value={draft.prf_currency_price}
            onChange={handleDraftNumeric}
            icon={Banknote}
            inputMode="numeric"
          />
          <Field
            id="rial_deposit"
            name="rial_deposit"
            label="ریال واریزی"
            placeholder="عدد"
            value={draft.rial_deposit}
            onChange={handleDraftNumeric}
            icon={Banknote}
            inputMode="numeric"
          />
          <Field
            id="prf_seller_country"
            name="prf_seller_country"
            label="کشور فروشنده"
            placeholder="..."
            value={draft.prf_seller_country}
            onChange={handleDraftChange}
            icon={Globe}
          />
          <DateField
            label="تاریخ سفارش"
            value={draft.prf_date}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, prf_date: value }))
            }
            icon={CalendarDays}
          />
          <DateField
            label="تاریخ اعتبار سفارش"
            value={draft.prf_expire_date}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, prf_expire_date: value }))
            }
            icon={CalendarDays}
          />
        </div>
      </ModalShell>

      <ModalShell
        open={deleteOpen}
        title="حذف سفارش"
        icon={Trash2}
        onClose={() => setDeleteOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-extrabold text-white bg-rose-600 hover:bg-rose-700 transition"
            >
              <Trash2 size={18} />
              حذف قطعی
            </button>
          </div>
        }
      >
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
          آیا از حذف این سفارش اطمینان دارید؟ این عملیات قابل بازگشت نیست.
        </div>
        <div className="mt-4 text-sm text-slate-700 text-right">
          شماره ثبت سفارش:{" "}
          <span className="font-extrabold">
            {order.prf_order_no || prfOrderNo}
          </span>
        </div>
      </ModalShell>
    </div>
  );
}
